import type { ColorToken, TaskDTO } from '@hearth/shared';
import { clsx } from 'clsx';
import { type PointerEvent as ReactPointerEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { colorClasses } from '../../lib/colors';
import { DEFAULT_LOCALE } from '../../lib/format';
import { CalendarPopover } from './CalendarPopover';
import { EntryIcons, OverdueDot, entryFillClass } from './EntryParts';
import {
  type CalendarEntry,
  type DraftSlot,
  type Reschedule,
  addDays,
  changesSchedule,
  coversDay,
  dayDiff,
  daySlot,
  entryLabel,
  entryTiming,
  formatCompactTime,
  hiddenPerColumn,
  isMultiDay,
  isOverdue,
  isSameDay,
  layoutMonthWeek,
  moveByDays,
  visibleLaneCount,
} from './model';
import { type Point, usePointerGesture } from './usePointerGesture';

/** Height of the date number row at the top of a cell, and of one entry lane. */
const CELL_HEADER_PX = 24;
const LANE_PX = 20;
/** Lanes shown before the grid has been measured (and in tests, where nothing has a size). */
const FALLBACK_LANES = 3;
const WEEKDAY = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'short' });
const WEEKDAY_LONG = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'long' });
const FULL_DATE = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'long', month: 'long', day: 'numeric' });
const DAY_TITLE = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const MONTH_SHORT = new Intl.DateTimeFormat(DEFAULT_LOCALE, { month: 'short' });

export type MonthViewProps = {
  /** The 42 days of the grid. */
  days: Date[];
  /** Month shown (0–11); days of the neighbouring months are dimmed. */
  month: number;
  entries: CalendarEntry[];
  now: Date;
  canEdit: boolean;
  colorOf: (task: TaskDTO) => ColorToken;
  selectedId: string | null;
  onOpenEntry: (task: TaskDTO, anchor: HTMLElement) => void;
  onOpenDay: (day: Date) => void;
  onCreate: (slot: DraftSlot, anchor: HTMLElement) => void;
  onReschedule: (task: TaskDTO, change: Reschedule) => void;
};

type MonthDrag = { task: TaskDTO; days: number };

/** Six weeks of days; multi-day entries as bars across each week row, the rest as chips. */
export function MonthView({
  days,
  month,
  entries,
  now,
  canEdit,
  colorOf,
  selectedId,
  onOpenEntry,
  onOpenDay,
  onCreate,
  onReschedule,
}: MonthViewProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const suppressClick = useRef(false);
  const [maxLanes, setMaxLanes] = useState(FALLBACK_LANES);
  const [drag, setDrag] = useState<MonthDrag | null>(null);
  const [dayList, setDayList] = useState<{ day: Date; anchor: HTMLElement } | null>(null);
  const begin = usePointerGesture();
  const byId = useMemo(() => new Map(entries.map((entry) => [entry.task.id, entry])), [entries]);
  const weeks = useMemo(
    () => Array.from({ length: Math.ceil(days.length / 7) }, (_, i) => days.slice(i * 7, i * 7 + 7)),
    [days],
  );

  // How many lanes fit follows the cell height, which follows the window.
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver !== 'function') return;
    const measure = () => {
      const rowHeight = grid.clientHeight / weeks.length;
      if (rowHeight > 0) setMaxLanes(Math.max(1, Math.floor((rowHeight - CELL_HEADER_PX - 2) / LANE_PX)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [weeks.length]);

  const layouts = useMemo(
    () =>
      weeks.map((week) => {
        const placements = layoutMonthWeek(
          week,
          entries.map((e) => ({
            id: e.task.id,
            start: e.timing.start,
            end: e.timing.end,
            bar: isMultiDay(e.timing),
          })),
        );
        const visibleLanes = visibleLaneCount(placements, maxLanes);
        return { placements, visibleLanes, hidden: hiddenPerColumn(placements, visibleLanes, week.length) };
      }),
    [weeks, entries, maxLanes],
  );

  const pointToDay = (point: Point): Date | null => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    const row = Math.min(
      weeks.length - 1,
      Math.max(0, Math.floor(((point.y - rect.top) / rect.height) * weeks.length)),
    );
    const col = Math.min(6, Math.max(0, Math.floor(((point.x - rect.left) / rect.width) * 7)));
    return days[row * 7 + col] ?? null;
  };

  const onEntryPointerDown = (event: ReactPointerEvent, task: TaskDTO) => {
    if (!canEdit) return;
    const origin = pointToDay({ x: event.clientX, y: event.clientY });
    if (!origin) return;
    const daysAt = (point: Point) => {
      const target = pointToDay(point);
      return target ? dayDiff(origin, target) : 0;
    };
    begin(event, {
      onStart: () => setDrag({ task, days: 0 }),
      onMove: (point) => setDrag({ task, days: daysAt(point) }),
      onDrop: (point) => {
        suppressClick.current = true;
        window.setTimeout(() => {
          suppressClick.current = false;
        }, 0);
        setDrag(null);
        const change = moveByDays(task, daysAt(point));
        if (change && changesSchedule(task, change)) onReschedule(task, change);
      },
      onCancel: () => setDrag(null),
    });
  };

  const openEntry = (task: TaskDTO, element: HTMLElement) => {
    if (!suppressClick.current) onOpenEntry(task, element);
  };

  // Days the dragged entry would land on, shaded as the drop target.
  const dropChange = drag ? moveByDays(drag.task, drag.days) : null;
  const dropTiming = drag && dropChange ? entryTiming({ ...drag.task, ...dropChange }) : null;

  const listEntries = dayList ? entries.filter((entry) => coversDay(entry.timing, dayList.day)) : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col select-none">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {weeks[0]?.map((day) => (
          <div
            key={day.getDay()}
            className="py-1.5 text-center text-2xs font-semibold tracking-wide text-slate-500 uppercase"
          >
            <abbr title={WEEKDAY_LONG.format(day)} className="no-underline">
              {WEEKDAY.format(day)}
            </abbr>
          </div>
        ))}
      </div>

      <div ref={gridRef} className="flex min-h-0 flex-1 flex-col">
        {weeks.map((week, row) => {
          const layout = layouts[row];
          return (
            <div
              key={week[0]?.getTime() ?? row}
              className="relative min-h-[5.5rem] flex-1 border-b border-slate-100 last:border-b-0"
            >
              <div className="absolute inset-0 grid grid-cols-7">
                {week.map((day) => {
                  const today = isSameDay(day, now);
                  const target = dropTiming ? coversDay(dropTiming, day) : false;
                  return (
                    <div
                      key={day.getTime()}
                      role="presentation"
                      onClick={(event) => {
                        if (canEdit && !suppressClick.current) onCreate(daySlot(day), event.currentTarget);
                      }}
                      className={clsx(
                        'border-l border-slate-100 first:border-l-0',
                        day.getMonth() !== month && 'bg-slate-50/60',
                        target && 'bg-ember-50 ring-1 ring-ember-300 ring-inset',
                        canEdit && 'cursor-cell',
                      )}
                    >
                      <div className="flex justify-center pt-0.5">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenDay(day);
                          }}
                          aria-label={FULL_DATE.format(day)}
                          aria-current={today ? 'date' : undefined}
                          className={clsx(
                            'flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs tabular',
                            today
                              ? 'bg-ember-500 font-semibold text-white'
                              : day.getMonth() === month
                                ? 'text-slate-800 hover:bg-slate-100'
                                : 'text-slate-400 hover:bg-slate-100',
                          )}
                        >
                          {day.getDate() === 1 && !today ? `${MONTH_SHORT.format(day)} 1` : day.getDate()}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pointer-events-none absolute inset-0">
                {layout?.placements.map((placement) => {
                  if (placement.lane >= layout.visibleLanes) return null;
                  const entry = byId.get(placement.id);
                  if (!entry) return null;
                  const { task, timing } = entry;
                  const overdue = isOverdue(task, now);
                  const color = colorOf(task);
                  const filled = placement.bar || task.type === 'note';
                  return (
                    <button
                      key={task.id}
                      type="button"
                      aria-label={entryLabel(entry, now) + (overdue ? ', overdue' : '')}
                      onPointerDown={(event) => onEntryPointerDown(event, task)}
                      onClick={(event) => openEntry(task, event.currentTarget)}
                      className={clsx(
                        'pointer-events-auto absolute flex items-center gap-1 overflow-hidden rounded px-1 text-left text-2xs',
                        filled
                          ? entryFillClass(task, color, selectedId === task.id)
                          : clsx(
                              'text-slate-700 hover:bg-slate-100',
                              task.status === 'completed' && 'opacity-60',
                              selectedId === task.id && 'bg-slate-100 ring-2 ring-ember-500',
                            ),
                        placement.continuesBefore && 'rounded-l-none',
                        placement.continuesAfter && 'rounded-r-none',
                        canEdit && 'cursor-grab touch-manipulation',
                        drag?.task.id === task.id && 'opacity-40',
                      )}
                      style={{
                        top: CELL_HEADER_PX + placement.lane * LANE_PX,
                        height: LANE_PX - 2,
                        left: `calc(${(placement.startCol / 7) * 100}% + 2px)`,
                        width: `calc(${((placement.endCol - placement.startCol) / 7) * 100}% - 4px)`,
                      }}
                    >
                      {!filled && (
                        <span
                          aria-hidden
                          className={clsx('size-2 shrink-0 rounded-full', colorClasses(color).dot)}
                        />
                      )}
                      {overdue && <OverdueDot />}
                      {!task.allDay && !placement.continuesBefore && (
                        <span className="shrink-0 opacity-80">{formatCompactTime(timing.start)}</span>
                      )}
                      <span
                        className={clsx(
                          'truncate font-medium',
                          task.status === 'completed' && 'line-through',
                        )}
                      >
                        {task.title}
                      </span>
                      <EntryIcons task={task} size={10} />
                    </button>
                  );
                })}
                {layout?.hidden.map((count, col) =>
                  count > 0 ? (
                    <button
                      key={col}
                      type="button"
                      onClick={(event) => {
                        const day = week[col];
                        if (day) setDayList({ day, anchor: event.currentTarget });
                      }}
                      className="pointer-events-auto absolute rounded px-1 text-left text-2xs font-medium text-slate-600 hover:bg-slate-100"
                      style={{
                        top: CELL_HEADER_PX + layout.visibleLanes * LANE_PX,
                        height: LANE_PX - 2,
                        left: `calc(${(col / 7) * 100}% + 2px)`,
                        width: `calc(${100 / 7}% - 4px)`,
                      }}
                    >
                      +{count} more
                    </button>
                  ) : null,
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dayList && (
        <CalendarPopover
          anchor={dayList.anchor}
          onClose={() => setDayList(null)}
          label={FULL_DATE.format(dayList.day)}
          className="w-64 p-2"
        >
          <button
            type="button"
            onClick={() => {
              setDayList(null);
              onOpenDay(dayList.day);
            }}
            className="mb-1 w-full rounded px-1 py-0.5 text-left text-[13px] font-semibold text-slate-900 hover:bg-slate-100"
          >
            {DAY_TITLE.format(dayList.day)}
          </button>
          <ul className="space-y-0.5">
            {listEntries.map((entry) => {
              const { task, timing } = entry;
              const color = colorOf(task);
              return (
                <li key={task.id}>
                  <button
                    type="button"
                    aria-label={entryLabel(entry, now)}
                    onClick={() => {
                      const anchor = dayList.anchor;
                      setDayList(null);
                      onOpenEntry(task, anchor);
                    }}
                    className={clsx(
                      'flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-xs',
                      isMultiDay(timing) || task.type === 'note'
                        ? entryFillClass(task, color, false)
                        : 'text-slate-700 hover:bg-slate-100',
                    )}
                  >
                    {!isMultiDay(timing) && task.type !== 'note' && (
                      <span
                        aria-hidden
                        className={clsx('size-2 shrink-0 rounded-full', colorClasses(color).dot)}
                      />
                    )}
                    {!task.allDay &&
                      timing.start >= dayList.day &&
                      timing.start < addDays(dayList.day, 1) && (
                        <span className="shrink-0 opacity-80">{formatCompactTime(timing.start)}</span>
                      )}
                    <span
                      className={clsx('truncate font-medium', task.status === 'completed' && 'line-through')}
                    >
                      {task.title}
                    </span>
                    <EntryIcons task={task} />
                  </button>
                </li>
              );
            })}
          </ul>
        </CalendarPopover>
      )}
    </div>
  );
}
